import { rem } from 'polished'
import React from 'react'
import type { FileError } from 'react-dropzone'
import styled from 'styled-components'

import { MAX_FILE_SIZE_MB } from './constants'

import TextButton from 'LuxKit/Button/TextButton'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import type { UploadTripDocumentError } from 'tripPlanner/api/documents/types'

const Root = styled(Group)`
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(12)};
  background-color: ${(props) =>
    props.theme.palette.messaging.critical.lightBackground};
`

const MainSection = styled.div`
  flex: 1;
`

interface Props {
  uploadError?: UploadTripDocumentError | null
  dropzoneError?: FileError | null
  onRequestRetry?: () => void
  onRequestDismiss?: () => void
}

function FileUploadError({
  uploadError,
  dropzoneError,
  onRequestRetry,
  onRequestDismiss,
}: Props) {
  let errorMsg = 'Error occurred while uploading your file.'
  if (
    uploadError?.status === 422 &&
    uploadError.code === 'InvalidFileNameError'
  ) {
    errorMsg = 'Invalid file name. Please rename your file and try again.'
  } else if (dropzoneError) {
    if (dropzoneError.code === 'file-too-large') {
      errorMsg = `File too large. Maximum size allowed is ${MAX_FILE_SIZE_MB}MB.`
    } else if (dropzoneError.code === 'file-invalid-type') {
      errorMsg = 'Invalid file. Upload only .PDF, .JPEG .PNG and .HEIC.'
    } else if (dropzoneError.code === 'invalid-file-name') {
      errorMsg =
        "Invalid file name. File names cannot contain '?', '#' or '%'. Please rename your file and try again."
    }
  }
  return (
    <Root direction="horizontal" gap={12} verticalAlign="center" fullWidth>
      <LinePaperclipIcon size="XS" colour="neutral-two" />
      <MainSection>
        <BodyText variant="large" lineClamp={1}>
          Upload failed
        </BodyText>
        <Caption variant="medium">{errorMsg}</Caption>
      </MainSection>
      {onRequestRetry && !!uploadError && (
        <TextButton kind="tertiary" onClick={onRequestRetry}>
          Try again
        </TextButton>
      )}
      {onRequestDismiss && (
        <TextButton kind="tertiary" onClick={onRequestDismiss}>
          Dismiss
        </TextButton>
      )}
    </Root>
  )
}

export default FileUploadError
